import { PageEvents } from '@griply/types';
import { goalTaskListGroupOptions, lifeAreaDetailFilters } from '@griply/utils';
import { useGoals } from 'features/goals';
import {
  getDefaultFilterDocument,
  getFilterDocument,
  useLifeAreaById,
  useUpdateLifeArea,
} from 'features/life-areas';
import { useSortedTags } from 'features/tags';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useParams } from 'react-router';
import { Plus, SettingsSort18 } from 'shared/assets/icons';
import { FilterButton } from 'shared/components/ui/filter-button';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import { PageSectionBase } from 'shared/components/ui/page-section-base';
import {
  PopupMenu,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuToggle,
} from 'shared/components/ui/popup-menu';
import { TreeSearchInput } from 'shared/components/ui/tree-search-input';
import { VirtualizedTreeListProvider } from 'shared/components/ui/virtualized-tree-list';
import { TaskListSearchProvider } from 'shared/contexts/task-list-search';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { Paths } from 'shared/routes/paths';
import { Goal } from 'shared/types/goal';
import { LifeArea } from 'shared/types/life-area';
import { getSubGoals } from 'shared/utils/get-sub-goals';
import { replaceUrlParams } from 'shared/utils/replace-url-params';

import { ArchivedItemsList } from './components/archived-items-list';
import { LifeAreaTasksContextMenu } from './components/life-area-tasks-context-menu';
import { TasksOverview } from './components/tasks-overview';
import * as Styled from './life-area-detail.style';
import { OutletContext } from './outlet-context';

const noLifeAreas: LifeArea[] = [];

type TasksProps = {
  fullSize?: boolean;
  grow?: boolean;
  hasLink?: boolean;
};

const TasksContent: React.FC<TasksProps> = ({ fullSize, grow, hasLink }) => {
  const links = useEntityLinks();
  const { t } = useTranslation();
  useTrackPageVisitEvent(PageEvents.LifeAreaDetailTasks, undefined);
  const { mainScrollRef } = useOutletContext<OutletContext>();

  const settingsButtonRef = useRef<HTMLButtonElement>(null);
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);

  const contextMenuRef = useRef<HTMLButtonElement>(null);
  const {
    menuOpen: contextMenuOpen,
    openMenu: openContextMenu,
    closeMenu: closeContextMenu,
  } = useOpenMenu();

  const { id } = useParams();
  const lifeArea = useLifeAreaById(id);
  const tags = useSortedTags();
  const goals = useGoals();

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { submit: updateLifeArea } = useUpdateLifeArea();

  const filterGoals = useMemo(() => {
    if (!lifeArea) {
      return [];
    }

    return goals.reduce<Goal[]>((acc, goal) => {
      if (goal.lifeAreaId === lifeArea.id) {
        acc.push(
          goal,
          ...getSubGoals(goal.id, { goals, recursive: true, links }),
        );
      }
      return acc;
    }, []);
  }, [goals, links, lifeArea]);

  const filterDocument = useMemo(() => getFilterDocument(lifeArea), [lifeArea]);

  const onUpdateFilterDocument = useCallback(
    (newFilterDocument: typeof filterDocument) => {
      if (!lifeArea) {
        return;
      }
      updateLifeArea({
        id: lifeArea.id,
        filterDocument: newFilterDocument,
      });
    },
    [lifeArea, updateLifeArea],
  );

  const onResetFilter = useCallback(() => {
    if (!lifeArea) {
      return;
    }
    updateLifeArea({
      id: lifeArea.id,
      filterDocument: getDefaultFilterDocument(lifeArea),
    });
  }, [lifeArea, updateLifeArea]);

  const tasksUrl = useMemo(
    () => replaceUrlParams(Paths.LifeAreaDetailTasks, { id: id! }),
    [id],
  );

  if (!lifeArea) {
    return null;
  }

  return (
    <PageSectionBase
      title={t('pages.life-area-detail.sections.tasks.label')}
      fullSize={fullSize}
      grow={grow}
      url={hasLink ? tasksUrl : undefined}
      interactions={
        <Styled.InteractionsContainer>
          <TreeSearchInput />
          <IconButton
            icon={Plus}
            ref={contextMenuRef}
            onClick={openContextMenu}
            color={Colors.Default}
          />

          <FilterButton
            filterDocument={filterDocument}
            defaultFilterDocument={getDefaultFilterDocument(lifeArea)}
            updateFilterDocument={onUpdateFilterDocument}
            onReset={onResetFilter}
            tags={tags}
            goals={filterGoals}
            lifeAreas={noLifeAreas}
            supportedGroupByModes={goalTaskListGroupOptions}
            supportedFilters={lifeAreaDetailFilters}
            renderAsIconButton
          />
          <IconButton
            icon={SettingsSort18}
            onClick={() => setIsSettingsMenuOpen(true)}
            ref={settingsButtonRef}
            color={Colors.Default}
          />
          {isSettingsMenuOpen && (
            <PopupMenu
              referenceElement={settingsButtonRef}
              onOutside={() => setIsSettingsMenuOpen(false)}
              position="bottom-end"
            >
              <PopupMenuList>
                <PopupMenuListItem>
                  <PopupMenuToggle
                    label={t('general.labels.tasks.show-completed-tasks')}
                    checked={!!lifeArea.showCompletedTasks}
                    onChange={(checked) => {
                      updateLifeArea({
                        id: lifeArea.id,
                        showCompletedTasks: checked,
                      });
                    }}
                  />
                </PopupMenuListItem>
              </PopupMenuList>
            </PopupMenu>
          )}

          {contextMenuOpen && (
            <LifeAreaTasksContextMenu
              referenceElement={contextMenuRef}
              lifeAreaId={lifeArea.id}
              onClose={closeContextMenu}
            />
          )}
        </Styled.InteractionsContainer>
      }
      noPadding
    >
      <Styled.ScrollContainer ref={scrollContainerRef}>
        <TasksOverview
          scrollElement={
            fullSize ? scrollContainerRef : mainScrollRef ?? scrollContainerRef
          }
          lifeArea={lifeArea}
        />

        <ArchivedItemsList lifeArea={lifeArea} />
      </Styled.ScrollContainer>
    </PageSectionBase>
  );
};

export const Tasks: React.FC<TasksProps> = (props) => (
  <TaskListSearchProvider>
    <VirtualizedTreeListProvider>
      <TasksContent {...props} />
    </VirtualizedTreeListProvider>
  </TaskListSearchProvider>
);
